/*
 * PROSOMO — product-card per-variant sync.
 *
 * The theme's own assets/product-card-swatch.min.js already updates the card price and
 * compare-at when a variant is picked (it only does so while settings.pcard_show_lowest_prices
 * is FALSE — its updatePrice() returns immediately when that setting is on). This file adds the
 * two things it does not handle:
 *
 *   1. the variant title next to the price  ([data-pp-variant-title])
 *   2. the "Membership price" value
 *
 * On (2): that line is injected by the VAT Dual Pricing app (app block
 * shopify://apps/vat-dual-pricing/blocks/storefrontDualPrice, script eo_sh_storefront.js) into
 * span#apply-price-exclude. We deliberately keep the app's own label/markup and only rewrite the
 * number, because the app computes it once per page from the PRODUCT-level price and so
 * (a) never follows the selected variant and (b) uses price / (1 + pct) — the VAT-extraction
 * formula — instead of price * (1 - pct), which is why $13.00 renders as $11.30 rather than
 * $11.05. We compute the real discount here from the theme setting
 * `membership_discount_percent`, passed through as data-pp-member-percent.
 *
 * NOTE: because the app owns that span, this is a deliberate coupling to another app's DOM.
 * If the app is removed or renames #apply-price-exclude, the membership number simply stops
 * being corrected — nothing else on the card breaks.
 */
(function () {
  'use strict';

  var CARD = '.sf__pcard';
  var ROW = '[data-prosomo-pcard-price]';
  var ROW_INNER = '.prosomo-pcard-price__row';
  var MEMBER_SPAN = '#apply-price-exclude';
  var WRAPPED = 'prosomo-pcard-price__row--wrapped';
  var DEFAULT_PERCENT = 15;

  function memberPercent(row) {
    var raw = row.getAttribute('data-pp-member-percent');
    if (raw === null || raw === '') return DEFAULT_PERCENT;
    var n = Number(raw);
    // 0 is a legitimate value, so test explicitly rather than relying on falsiness.
    return isFinite(n) && n >= 0 && n < 100 ? n : DEFAULT_PERCENT;
  }

  function formatMember(cents) {
    var symbol = (window.appCurrency && window.appCurrency.format) || '$';
    // Mirror the app's own "$ 11.05" spacing so the corrected value looks untouched.
    return symbol + ' ' + (cents / 100).toFixed(2);
  }

  function variantsFor(card) {
    var node = card.querySelector('[data-pcard-variant-picker] script[type="application/json"]');
    if (!node) return null;
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /*
   * Resolve the selected variant independently of the theme's swatch element, so we never race
   * its listeners. Start from the variant rendered server-side, then override whichever option
   * position the card actually exposes (the card shows only settings.pcard_option_name).
   */
  function selectedVariant(card, row, variants) {
    var initialId = Number(row.getAttribute('data-pp-initial-variant'));
    var initial = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === initialId) { initial = variants[i]; break; }
    }
    if (!initial) initial = variants[0];

    var wanted = [initial.option1, initial.option2, initial.option3];

    var select = card.querySelector('.sf-product-variant-option-dropdown');
    if (select && select.selectedIndex >= 0) {
      var opt = select.options[select.selectedIndex];
      if (opt) {
        var pos = Number(opt.getAttribute('data-option-position')) || 1;
        wanted[pos - 1] = opt.getAttribute('data-value') || opt.value;
      }
    } else {
      var active = card.querySelectorAll('.product-option-item[data-selected="true"]');
      for (var j = 0; j < active.length; j++) {
        var p = Number(active[j].getAttribute('data-option-position')) || 1;
        wanted[p - 1] = active[j].getAttribute('data-value');
      }
    }

    var target = wanted.filter(function (v) { return v !== null && v !== undefined; });
    for (var k = 0; k < variants.length; k++) {
      var options = variants[k].options || [];
      if (options.length !== target.length) continue;
      var match = true;
      for (var m = 0; m < target.length; m++) {
        if (options[m] !== target[m]) { match = false; break; }
      }
      if (match) return variants[k];
    }
    return initial;
  }

  /*
   * ---- Wrapped-title detection ----
   *
   * The variant titles on this store are long ("8 Packs (16 Steaks ($10.00 per steak)") and the
   * title is white-space: nowrap, so on a narrow card it moves WHOLESALE onto a second line.
   * When that happens the leading "|" separator becomes a stray character at the start of line 2
   * and the title's margin-top (5%, which resolves against the card's WIDTH) opens a visible
   * gap. Both are dropped by the .prosomo-pcard-price__row--wrapped rules in custom.css.liquid.
   *
   * CSS cannot express this: there is no selector for "this flex item wrapped onto a new line".
   * So measure it.
   *
   * The predicate is HORIZONTAL on purpose -- offsetLeft, not offsetTop. The row is
   * align-items: center and the title is smaller than the price, so a title sharing line 1 already
   * sits BELOW the price's top edge; a vertical test would have to guess a tolerance against the
   * price's line-box height and the percentage margin. Left edges are unambiguous: beside the
   * price the title starts to its right, wrapped it starts at the row's left edge, same as the
   * price.
   *
   * ANTI-FLAP: always measure with the class REMOVED, i.e. in the canonical pipe-present state.
   * Hiding the "|" makes the title ~13px narrower, which can be exactly enough for it to fit back
   * on line 1 -- measuring the state we just applied would oscillate wrapped/unwrapped forever.
   * Measuring the intrinsic layout makes the result a property of the content, not of our class.
   *
   * Batched across every row on the page (write-reset, then read, then write-result) so the whole
   * grid costs ONE forced reflow rather than one per card. Both phases run inside a single task,
   * so the browser never paints the intermediate un-wrapped state.
   */
  function measureWrap() {
    var rows = document.querySelectorAll(CARD + ' ' + ROW_INNER);
    if (!rows.length) return;

    var items = [];
    for (var i = 0; i < rows.length; i++) {
      var title = rows[i].querySelector('[data-pp-variant-title]');
      var price = rows[i].querySelector('.f-price');
      // Single-variant cards render no title, so there is nothing to separate or nudge.
      if (!title || !price) continue;
      rows[i].classList.remove(WRAPPED);
      items.push({ row: rows[i], title: title, price: price, wrapped: false });
    }
    if (!items.length) return;

    // Read phase — the first getBoundingClientRect flushes layout once; the rest are free,
    // since nothing is written in between.
    for (var j = 0; j < items.length; j++) {
      var it = items[j];
      var pr = it.price.getBoundingClientRect();
      var tr = it.title.getBoundingClientRect();
      /*
       * Not laid out yet — a card inside a closed tab, an un-activated carousel slide, or simply
       * measured before first paint. Every rect is 0 and any comparison between them is
       * meaningless, so decide nothing and leave the separator alone; the ResizeObserver below
       * re-measures the moment the row has a real width.
       */
      if (!pr.width || !tr.width) continue;
      /*
       * Same line == the title starts at or after the price's RIGHT edge.
       *
       * Testing against the price's right edge rather than its left is what makes this FAIL SAFE.
       * The previous "title.offsetLeft <= price.offsetLeft" form read all-zero geometry as
       * "wrapped" — so an unlaid-out card lost its "|" and then, once visible, fitted on one line
       * with nothing left to re-measure it: separator gone, no line break. Against the right edge,
       * degenerate geometry reads as "same line", i.e. keep the separator, which is the harmless
       * direction to be wrong in.
       */
      it.wrapped = tr.left < pr.right - 1;
    }

    for (var k = 0; k < items.length; k++) {
      if (items[k].wrapped) items[k].row.classList.add(WRAPPED);
    }
  }

  var wrapPending = false;
  function scheduleWrap() {
    if (wrapPending) return;
    wrapPending = true;
    window.requestAnimationFrame(function () {
      wrapPending = false;
      measureWrap();
    });
  }

  /*
   * A row can reach its real width long after load — a tab opening, a carousel initialising, a
   * quick-view drawer sliding in. The window 'resize' listener never fires for any of those, so
   * observe the rows themselves.
   *
   * Gated on WIDTH specifically. Width is the only thing that decides whether the title fits, and
   * applying .--wrapped makes the row TALLER — an ungated observer would feed its own output back
   * in (measure -> class -> taller -> measure -> ...).
   */
  var rowObserver = null;
  if (window.ResizeObserver) {
    rowObserver = new window.ResizeObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var el = entries[i].target;
        var w = entries[i].contentRect ? entries[i].contentRect.width : el.clientWidth;
        if (Math.abs(w - (el.__ppWidth || 0)) < 0.5) continue;
        el.__ppWidth = w;
        scheduleWrap();
      }
    });
  }

  function observeRow(row) {
    if (!rowObserver || row.__ppObserved) return;
    row.__ppObserved = true;
    rowObserver.observe(row);
  }

  function apply(card) {
    var row = card.querySelector(ROW);
    if (!row) return;
    var variants = variantsFor(card);
    if (!variants) return;
    var variant = selectedVariant(card, row, variants);
    if (!variant) return;

    var title = row.querySelector('[data-pp-variant-title]');
    if (title && title.textContent !== variant.title) title.textContent = variant.title;
    /*
     * Re-measure unconditionally: a different title is a different width, AND the vendor's
     * updatePrice() rewrites the price on this same event, which moves the title's left edge even
     * when the title text itself is unchanged. scheduleWrap() is rAF-coalesced, so N cards
     * responding to one event still cost a single measurement pass.
     */
    scheduleWrap();

    /*
     * Cache the target so the observer pass below is cheap: re-deriving it would mean a
     * JSON.parse of the card's variant blob on every frame of a mutation burst.
     */
    card.__ppMemberCents = Math.round((variant.price * (100 - memberPercent(row))) / 100);
    writeMember(card);
  }

  /*
   * Returns true only when it actually changed the DOM, so callers can budget real corrections.
   * Only writes on a real difference — that is what stops the observer from feeding itself.
   */
  function writeMember(card) {
    var cents = card.__ppMemberCents;
    if (typeof cents !== 'number' || !isFinite(cents)) return false;
    var span = card.querySelector(MEMBER_SPAN);
    if (!span) return false;
    var text = formatMember(cents);
    if (span.textContent.trim() === text) return false;
    span.textContent = text;
    return true;
  }

  /*
   * ---- Why this replaced the old 400ms / 1500ms re-asserts ----
   *
   * The app's span is not in the server HTML: eo_sh_storefront.js is deferred from a CDN and
   * injects #apply-price-exclude asynchronously. Re-asserting at fixed delays and then giving up
   * loses the race outright whenever the app lands later than the last timeout — there is no
   * upper bound on when a third-party script finishes, so no delay is safe. Watch instead of
   * guess; see the matching comment in assets/prosomo-product-membership.js.
   *
   * Cost control: rAF-coalesced (one pass per frame however many mutations arrive, nothing while
   * idle), each pass is one querySelector + a string compare per card, and a write budget
   * disconnects the whole thing if corrections ever storm.
   */
  var boundCards = [];
  var memberPending = false;
  var memberWrites = 0;
  var MAX_MEMBER_WRITES = 200;

  function scheduleMembership() {
    if (memberPending || memberWrites >= MAX_MEMBER_WRITES) return;
    memberPending = true;
    window.requestAnimationFrame(function () {
      memberPending = false;
      for (var i = 0; i < boundCards.length; i++) {
        if (writeMember(boundCards[i])) memberWrites++;
      }
    });
  }

  function bind(card) {
    if (card.__prosomoPcardBound) return;
    card.__prosomoPcardBound = true;

    var run = function () {
      memberWrites = 0; // new target value — re-arm the correction budget
      window.requestAnimationFrame(function () { apply(card); });
    };

    var select = card.querySelector('.sf-product-variant-option-dropdown');
    if (select) select.addEventListener('change', run);
    card.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('.product-option-item')) run();
    });

    boundCards.push(card);
    run();

    // No MutationObserver: fall back to the old bounded re-asserts rather than never correcting.
    if (!window.MutationObserver) {
      window.setTimeout(run, 400);
      window.setTimeout(run, 1500);
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) bind(cards[i]);
    var isCard = scope !== document && scope.matches && scope.matches(CARD);
    if (isCard) bind(scope);
    /*
     * Scheduled independently of bind()/apply() on purpose: apply() returns early when a card
     * exposes no variant JSON, and the wrap fix is purely presentational — it must not be
     * conditional on the variant-sync path succeeding.
     *
     * Gated on actually having found a card, because the MutationObserver below calls init() for
     * EVERY element inserted anywhere in the document (apps, cart drawer, chat widgets). Without
     * the gate a chatty third party would trigger a forced-reflow measurement pass every frame.
     */
    var rows = scope.querySelectorAll(ROW_INNER);
    for (var r = 0; r < rows.length; r++) observeRow(rows[r]);

    if (cards.length || isCard) scheduleWrap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Cards also arrive from filtering, pagination, load-more and quick-view.
  if (window.MutationObserver) {
    new window.MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) init(added[j]);
        }
      }
      // The app's injection/overwrite of #apply-price-exclude shows up here too — one coalesced
      // re-check per frame corrects it however late it arrives.
      scheduleMembership();
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  /*
   * Card width is what decides whether the title fits, so every reflow of the grid needs a
   * re-measure: viewport resize / orientation change, and the web-font swap (the fallback font
   * has different metrics, so a title can fit before the swap and not after).
   */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(scheduleWrap, 150);
  });

  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(scheduleWrap);
  }

  // Card images finishing can still shift the grid.
  window.addEventListener('load', scheduleWrap);
})();
